<template>
  <div>
    <app-cells><h2 class="title">Unpaid</h2></app-cells>
    <v-client-table
      :data="table.items"
      :columns="table.columns"
      :options="table.options"
      class="table-default"
      :class="{ 'table-default--loading': table_preloader }"
    >
      <template #h__count><div class="table-default__left">Invoice #</div></template>
      <template #h__payer><div class="table-default__left">Payer</div></template>
      <template #h__manager><div class="table-default__left">Manager</div></template>
      <template #count="{ row }">
        <div class="table-default__left table-default__controls">
          <a
            :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.number }}
          </a>
        </div>
      </template>
      <template #payer="{ row }">
        <div class="table-default__left">{{ row.payer }}</div>
      </template>
      <template #manager="{ row }">
        <div class="table-default__left table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt=""
              v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
            >
          </div>
          <router-link
            :to="{ name: 'clients-groups-general', params: { id: row.itinerary.lead.client_group.id } }"
            class="table-default__link-border"
          >
            {{ row.itinerary.lead.client_group.name }}
          </router-link>
        </div>
      </template>
      <template #deadline="{ row }">{{ dateFormat(row.deadline) }}</template>
      <template #payable="{ row }">
        {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
      </template>
      <template #printed="{ row }">
        <app-checkbox
          v-if="!table_preloader"
          @change.native="onUpdatePrinted(row)"
          v-model="row.payment_on_spot_info.printed"
          class="checkbox--table"
        />
      </template>
      <template #printed_by="{ row }">
        <div v-if="row.payment_on_spot_info?.printed_by" class="table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.payment_on_spot_info.printed_by.crop.crop_url)" alt=""
              v-tooltip="{ content: row.payment_on_spot_info.printed_by.full_name, placement: 'top-start', offset: 10 }"
            >
          </div>
          <span>{{ dateTimeFormat(row.payment_on_spot_info.printed_at) }}</span>
        </div>
        <span v-else>-</span>
      </template>
    </v-client-table>
    <app-cells><h2 class="title">Unpaid (clarify charge method with sales manager)</h2></app-cells>
    <v-client-table
      :data="table_unpaid.items"
      :columns="table_unpaid.columns"
      :options="table_unpaid.options"
      class="table-default"
      :class="{ 'table-default--loading': table_preloader }"
    >
      <template #h__count><div class="table-default__left">Invoice #</div></template>
      <template #h__payer><div class="table-default__left">Payer</div></template>
      <template #h__manager><div class="table-default__left">Manager</div></template>
      <template #count="{ row }">
        <div class="table-default__left table-default__controls">
          <a
            :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.number }}
          </a>
        </div>
      </template>
      <template #payer="{ row }">
        <div class="table-default__left">{{ row.payer }}</div>
      </template>
      <template #manager="{ row }">
        <div class="table-default__left table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt=""
              v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
            >
          </div>
          <router-link
            :to="{ name: 'clients-groups-general', params: { id: row.itinerary.lead.client_group.id } }"
            class="table-default__link-border"
          >
            {{ row.itinerary.lead.client_group.name }}
          </router-link>
        </div>
      </template>
      <template #payable="{ row }">
        {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
      </template>
      <template #deadline="{ row }">{{ dateFormat(row.deadline) }}</template>
      <template #charge_type="{ row }">{{ row.charge_type.name }}</template>
    </v-client-table>
    <app-cells><h2 class="title">Paid</h2></app-cells>
    <v-client-table
      :data="table_paid.items"
      :columns="table_paid.columns"
      :options="table_paid.options"
      class="table-default"
    >
      <template #h__count><div class="table-default__left">Invoice #</div></template>
      <template #h__payer><div class="table-default__left">Payer</div></template>
      <template #h__manager><div class="table-default__left">Manager</div></template>
      <template #h__status><div class="table-default__left">Status</div></template>
      <template #h__method><div class="table-default__left">Method</div></template>
      <template #count="{ row }">
        <div class="table-default__left table-default__controls">
          <a
            :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.number }}
          </a>
        </div>
      </template>
      <template #status="{ row }">
        <div class="table-default__left table-default__controls">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
        </div>
      </template>
      <template #payer="{ row }">
        <div class="table-default__left">{{ row.payer }}</div>
      </template>
      <template #manager="{ row }">
        <div class="table-default__left table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt=""
              v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
            >
          </div>
          <router-link
            :to="{ name: 'clients-groups-general', params: { id: row.itinerary.lead.client_group.id } }"
            class="table-default__link-border"
          >
            {{ row.itinerary.lead.client_group.name }}
          </router-link>
        </div>
      </template>
      <template #paid="{ row }">
        {{ row.amount_paid_usd ? `${row.amount_paid_usd.toLocaleString()} USD` : '-'}}
      </template>
      <template #method="{ row }">
        <div class="table-default__left">
          {{ row.payment_method ? `${row.payment_method.name} ${dateFormat(row.date_of_payment)}` : '-' }}
        </div>
      </template>
    </v-client-table>
  </div>
</template>

<script>
import { getFinanceInvoiceList, patchFinanceInvoicePaymentOnSpot } from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'ClimbingEventsDetailInvoices',
  data() {
    return {
      table: {
        items: [],
        columns: [
          'count',
          'payer',
          'manager',
          'deadline',
          'payable',
          'printed',
          'printed_by',
        ],
        options: {
          headings: {
            'count': 'Invoice #',
            'payer': 'Payer',
            'manager': 'Manager',
            'deadline': 'Deadline',
            'payable': 'Payable',
            'printed': 'Printed',
            'printed_by': 'Printed by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_unpaid: {
        items: [],
        columns: [
          'count',
          'payer',
          'manager',
          'payable',
          'deadline',
          'charge_type',
        ],
        options: {
          headings: {
            'count': 'Invoice #',
            'payer': 'Payer',
            'manager': 'Manager',
            'payable': 'Payable',
            'deadline': 'Deadline',
            'charge_type': 'Charge type',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_paid: {
        items: [],
        columns: [
          'count',
          'status',
          'payer',
          'manager',
          'paid',
          'method',
        ],
        options: {
          headings: {
            'count': 'Invoice #',
            'status': 'Status',
            'payer': 'Payer',
            'manager': 'Manager',
            'paid': 'Paid',
            'method': 'Method',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_preloader: false,
      invoice_to_delete: null,
    }
  },
  created() {
    this.fetchItems()
    this.fetchItemsUnpaid()
    this.fetchItemsPaid()
  },
  methods: {
    fetchItems() {
      getFinanceInvoiceList({ briefing: this.$route.params.id, charge_type: 'spot', status: 'sent' })
        .then(response => {
          this.table.items = response.data
          this.table_preloader = false
        })
    },
    fetchItemsUnpaid() {
      getFinanceInvoiceList({ briefing: this.$route.params.id, charge_type: 'standard,auth_form', status: 'sent' })
        .then(response => {
          this.table_unpaid.items = response.data
        })
    },
    fetchItemsPaid() {
      getFinanceInvoiceList({ briefing: this.$route.params.id, status: 'paid,partially' })
        .then(response => {
          this.table_paid.items = response.data
        })
    },
    onUpdatePrinted(row) {
      this.table_preloader = true
      patchFinanceInvoicePaymentOnSpot(row.id, { printed: row.payment_on_spot_info.printed })
        .then(() => {
          this.fetchItems()
        })
        .catch(() => { this.table_preloader = false })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

